import {
  ApiRequestError,
  executeSdkEnvelopeRequest,
  executeSdkRawRequest,
} from './api-request.util';
import { adminReferralsApi } from './sdk-client';

export class AdminReferralsError extends ApiRequestError {
  constructor(status: number, message: string) {
    super(status, message, 'AdminReferralsError');
  }
}

export type AdminReferralStatusFilter = 'pending' | 'completed' | 'invalidated';

export type AdminReferralsListParams = {
  status?: AdminReferralStatusFilter;
  page?: number;
  limit?: number;
};

export type AdminReferralListItem = {
  id: string;
  referrerId: string;
  refereeId: string;
  status: string | null;
  completedAt: string | null;
  rewardGrantedAt: string | null;
  createdAt: string;
  referrerEmail: string | null;
  referrerName: string | null;
  refereeEmail: string | null;
  refereeName: string | null;
};

export type AdminReferralsListMeta = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

export type AdminReferralsListResult = {
  data: AdminReferralListItem[];
  meta: AdminReferralsListMeta;
};

export type AdminReferralStats = {
  total: number;
  by_status: Record<string, number>;
  rewards_issued: number;
  credits_active: number;
};

export type AdminReferralUserSummary = {
  id: string;
  email: string | null;
  firstName: string | null;
};

export type AdminReferralReward = {
  id: string;
  referralId: string;
  recipientId: string;
  rewardType: string;
  status: string | null;
  sourceType: string;
  createdAt: string;
  updatedAt: string;
};

export type AdminReferralDetail = {
  id: string;
  referrerId: string;
  refereeId: string;
  referralCodeId: string | null;
  referralCode: string | null;
  status: string | null;
  completedAt: string | null;
  rewardGrantedAt: string | null;
  createdAt: string;
  updatedAt: string;
  referrer: AdminReferralUserSummary | null;
  referee: AdminReferralUserSummary | null;
  rewards: AdminReferralReward[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseListMeta(value: unknown): AdminReferralsListMeta {
  if (!isRecord(value)) {
    return { page: 1, limit: 20, total: 0, totalPages: 0 };
  }

  return {
    page: typeof value.page === 'number' ? value.page : 1,
    limit: typeof value.limit === 'number' ? value.limit : 20,
    total: typeof value.total === 'number' ? value.total : 0,
    totalPages: typeof value.totalPages === 'number' ? value.totalPages : 0,
  };
}

export async function listAdminReferrals(
  params: AdminReferralsListParams = {},
): Promise<AdminReferralsListResult> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminReferralsApi.adminReferralsControllerListReferralsV1Raw({
        ...(params.status ? { status: params.status } : {}),
        ...(params.page != null ? { page: params.page } : {}),
        ...(params.limit != null ? { limit: params.limit } : {}),
      }),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      return {
        data: Array.isArray(envelope.data)
          ? (envelope.data as AdminReferralListItem[])
          : [],
        meta: parseListMeta(envelope.meta),
      };
    },
    fallback: 'Failed to load referrals',
    ErrorClass: AdminReferralsError,
  });
}

export async function getAdminReferralStats(): Promise<AdminReferralStats> {
  return executeSdkRawRequest({
    request: () =>
      adminReferralsApi.adminReferralsControllerGetReferralStatsV1Raw(),
    fallback: 'Failed to load referral stats',
    ErrorClass: AdminReferralsError,
  });
}

export async function getAdminReferralDetail(
  id: string,
): Promise<AdminReferralDetail> {
  return executeSdkRawRequest({
    request: () =>
      adminReferralsApi.adminReferralsControllerGetReferralDetailV1Raw({ id }),
    fallback: 'Failed to load referral detail',
    ErrorClass: AdminReferralsError,
  });
}
